/**
 * @file     useReltaChat.ts
 * @purpose  Client state machine for the Relta chat, thin protocol edition:
 *           each turn sends {conversationId, message} (server owns signed-in
 *           transcripts; anonymous continuity rides a capped anonState). Match
 *           cards are client-side decoration fetched from the audited listings
 *           API; the model and the stored conversation never carry MLS rows.
 */
'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { secureFetch } from '@/services/anti-scraping/secureFetch';
import { filtersToSearchParams } from '@/components/search/filterUrlCodec';
import { DEFAULT_VALUE, type TraditionalSearchValue } from '@/components/search/searchValue';
import { useAuth } from '@/hooks/useAuth';
import { reltaTrack } from '@/hooks/useReltaTrack';
import { mlsToCardProps, toDisplay } from '@/features/properties/services/cardAdapters';
import { formatCAD } from '@/utils/finance/mortgage';
import { filterChips } from '@/services/mls/filterChips';
import { appendNote, extractNote } from '@/services/ai-chat/contextBuilder';
import { computeManualEdits } from '@/features/ai-chat/filterDelta';
import { PRESENT_PER_TURN, type MapCommand, type MortgageNumbers, type TurnPayload } from '@/services/ai-chat/types';
import type { MlsListingCardData } from '@/types/listing';
import type { ReltaChatMessage, ReltaConversationState, ReltaConversationSummary } from './types';
import type { SeoChatSurfaceId } from '@/services/ai-chat/seoChatSurface';
import {
  loadLocalConversation,
  loadLocalById,
  listLocalSummaries,
  removeLocalConversation,
  newLocalId,
  saveLocalConversation,
  adoptAnonymousConversation,
  listConversations,
  fetchConversation,
  deleteConversation as deleteConversationApi,
} from './conversationStore';

const MATCHES_PER_TURN = PRESENT_PER_TURN;
/** Page size for the unseen-home pick: enough headroom that a cursor reset
 *  after a search change cannot re-present recently shown homes. */
const PRESENT_FETCH_SIZE = 10;
const MAX_SEEN_KEYS = 50;
/** Anonymous buyers get this many ANSWERED turns; the next send attempt opens
 *  the gate BEFORE the API call. The server enforces its own cap. */
// Free tier: an anonymous visitor can converse freely and see the FIRST home,
// then the signup wall appears when a SECOND property card would be shown. The
// per-identity daily turn budget (server-side) is the separate anti-abuse cap.
const ANON_FREE_CARDS = 1;
/** Resume only a recent conversation; stale ones must not replace the hero. */
const RESUME_WINDOW_MS = 48 * 60 * 60 * 1000;

/** "4188 Nelson St" / "#301-1188 Richards Street" / bare MLS number: the buyer
 *  is looking up a specific listing, so route through autocomplete, not chat. */
const ADDRESS_LIKE = /^\s*(#?\d+(-\d+)?\s+\d*\s*[A-Za-z]+\s+(st|street|ave|avenue|rd|road|dr|drive|way|blvd|boulevard|cres|crescent|pl|place|lane|ct|court|hwy|highway|mews|terrace|terr|gate)\b|[rR]?\d{7,}\s*$)/i;

/** Pull an address / MLS number out of a message, tolerating a conversational
 *  lead-in ("show me the listings at ..."). The "...at|on <address>" branch is
 *  END-anchored so a filtered search ("condo at 1480 Main under $1M") is NOT
 *  hijacked into a single-listing jump. Returns null when there is no address. */
export function addressQuery(text: string): string | null {
  const t = text.trim();
  if (ADDRESS_LIKE.test(t)) return t;
  const m = t.match(/\b(?:at|on)\s+(#?\d+(?:-\d+)?\s+[\w\s]*?\b(?:st|street|ave|avenue|rd|road|dr|drive|way|blvd|boulevard|cres|crescent|pl|place|lane|ct|court|hwy|highway|mews|terrace|terr|gate))\s*$/i);
  return m ? m[1].trim() : null;
}

async function lookupListing(q: string): Promise<string | null> {
  try {
    const res = await fetch(`/api/smart-match/autocomplete?q=${encodeURIComponent(q)}`);
    if (!res.ok) return null;
    const data = (await res.json()) as { suggestions?: Array<{ listingSlug?: string | null; listingKey: string }> };
    const top = data.suggestions?.[0];
    return top ? `/properties/${top.listingSlug ?? top.listingKey}` : null;
  } catch {
    return null;
  }
}

/** One request per search turn (audited listings API; count rides page 1). */
async function matchesFor(
  filters: Partial<TraditionalSearchValue>,
  offset: number,
): Promise<{ results: MlsListingCardData[]; count: number | null }> {
  const sp = filtersToSearchParams({ ...DEFAULT_VALUE, ...filters });
  sp.set('limit', String(PRESENT_FETCH_SIZE));
  // The respond payload already carries the authoritative count.
  sp.set('skipCount', '1');
  if (offset > 0) sp.set('offset', String(offset));
  try {
    const res = await fetch(`/api/mls/listings?${sp.toString()}`);
    if (!res.ok) return { results: [], count: null };
    const data = (await res.json()) as { results?: MlsListingCardData[]; count?: number | null };
    return { results: data.results ?? [], count: data.count ?? null };
  } catch {
    return { results: [], count: null };
  }
}

/** Re-hydrate presented cards on resume: v2 transcripts (local + server) store
 *  only card KEYS, so refetch the blobs from the audited listings API and glue
 *  them back onto the messages that showed them. Messages that already carry
 *  `matches` are left alone. Used by BOTH the mount resume and openConversation
 *  so /properties (no history rail) recovers its houses too. */
async function hydrateCards(
  msgs: ReltaChatMessage[],
  filters: Partial<TraditionalSearchValue>,
  seenKeys: string[],
): Promise<ReltaChatMessage[]> {
  const keyByMsgIdx = new Map<number, string[]>();
  const allKeys = new Set<string>();
  msgs.forEach((m, i) => {
    if (m.matches?.length) return; // already have the cards
    if (m.presentedKeys?.length) {
      keyByMsgIdx.set(i, m.presentedKeys);
      m.presentedKeys.forEach((k) => allKeys.add(k));
    }
  });
  // Tail fallback: the most recent card-bearing turn without stored keys gets
  // the last raw (non-twin) key from the ledger.
  const tailRevIdx = [...msgs].reverse().findIndex((m) => m.hadMatches && !m.matches?.length && !m.presentedKeys?.length);
  if (tailRevIdx >= 0) {
    const idx = msgs.length - 1 - tailRevIdx;
    const tailKey = seenKeys.filter((k) => !k.startsWith('mls:')).at(-1);
    if (tailKey) { keyByMsgIdx.set(idx, [tailKey]); allKeys.add(tailKey); }
  }
  if (allKeys.size === 0) return msgs;
  try {
    const res = await fetch(`/api/mls/listings?keys=${encodeURIComponent([...allKeys].join(','))}&limit=200&skipCount=1`);
    const data = res.ok ? ((await res.json()) as { results?: MlsListingCardData[] }) : null;
    const byKey = new Map((data?.results ?? []).map((c) => [c.listingKey, c] as const));
    const reasons = filterChips(filters);
    return msgs.map((m, i) => {
      const keys = keyByMsgIdx.get(i);
      if (!keys) return m;
      const cards = keys.map((k) => byKey.get(k)).filter((c): c is MlsListingCardData => c != null);
      return cards.length ? { ...m, matches: cards, reasons } : m;
    });
  } catch {
    return msgs; // resume still works text-only
  }
}

/** Deterministic fact block for a focused match, projected through the same
 *  cardAdapters.toDisplay the cards use. Lives on message.facts, never in the
 *  content the model sees. */
function matchFacts(m: MlsListingCardData, t: (k: string, o: { defaultValue: string }) => string): string {
  const d = toDisplay(mlsToCardProps(m));
  const bits: string[] = [];
  if (d.price != null) bits.push(`$${d.price.toLocaleString('en-CA')}`);
  if (d.beds != null) bits.push(`${d.beds} ${t('properties.card.bd', { defaultValue: 'bd' })}`);
  if (d.baths != null) bits.push(`${d.baths} ${t('properties.card.ba', { defaultValue: 'ba' })}`);
  if (d.sqft != null) bits.push(`${Math.round(d.sqft).toLocaleString('en-CA')} ${t('properties.card.sqft', { defaultValue: 'sqft' })}`);
  if (d.yearBuilt != null) bits.push(`${t('properties.card.built', { defaultValue: 'Built' })} ${d.yearBuilt}`);
  const where = [d.title, d.subtitle].filter(Boolean).join(', ');
  return `${where}: ${bits.join(' · ')}`;
}

function mapsUrlFor(filters: Partial<TraditionalSearchValue>): string {
  const place = filters.neighbourhoods?.[0] ?? filters.regions?.[0]?.replace(/^bc:/, '').replace(/-/g, ' ') ?? 'Vancouver BC';
  return `https://www.google.com/maps/search/${encodeURIComponent(`${place} BC`)}`;
}

const cad = formatCAD;

interface RespondPayload extends TurnPayload {
  conversationId: string | null;
  mortgage: MortgageNumbers | null;
}

/** Optional bindings that let the chat drive an external search surface (the
 *  /properties map). Omitted on the home page, where the chat owns its own
 *  filter state and behaviour is unchanged. */
export interface ReltaChatOptions {
  /** Allowlisted SEO surface. The server maps this id to trusted context; no
   *  free-form page content is sent by the client. */
  surface?: SeoChatSurfaceId;
  /** Optional closed-vocabulary locator, such as a registered BC city slug.
   * The server resolves it; client-provided names or facts are never trusted. */
  surfaceLocation?: string;
  /** Optional allowlisted facet slug for a home-search SEO page. */
  surfaceFacet?: string;
  /** Local conversation namespace. Public page assistants use a page-specific
   * scope so an unrelated prior chat never replaces the contextual greeting. */
  conversationScope?: string;
  /** Whether account-wide server history belongs in this workspace. Scoped
   * embedded assistants keep this false and show only same-scope local chats. */
  serverHistory?: boolean;
  /** Client-side presentation backstop. The /properties filter assistant never
   * fetches or renders listing cards, even if a stale server returned present. */
  allowListingCards?: boolean;
  /** Completed anonymous exchanges allowed before the next send opens the
   *  host-provided account gate. Omitted on existing chat surfaces. */
  anonymousTurnLimit?: number;
  /** Called with the AI's resulting filters after every turn, so the host can
   *  push them into a shared store (the map/list/URL react). */
  onFiltersChange?: (filters: Partial<TraditionalSearchValue>) => void;
  /** Read the host's current filters at the start of a turn, so manual chip
   *  edits made on the map become the AI's working baseline. */
  getExternalFilters?: () => Partial<TraditionalSearchValue>;
  /** Apply map directives (layer toggles from the payload; a focus derived from
   *  the focused card's coords) to the live map. */
  onMapCommands?: (commands: MapCommand[]) => void;
}

export function useReltaChat(opts?: ReltaChatOptions) {
  const { t } = useTranslation();
  const router = useRouter();
  const { user } = useAuth();
  // Held in a ref so send()'s stable useCallback always sees the latest host
  // bindings without re-subscribing on every render.
  const optsRef = useRef(opts);
  optsRef.current = opts;
  const conversationScope = opts?.conversationScope ?? 'general';
  const serverHistory = opts?.serverHistory !== false;
  const allowListingCards = opts?.allowListingCards !== false;
  const [messages, setMessages] = useState<ReltaChatMessage[]>([]);
  const [thinking, setThinking] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);
  const [conversations, setConversations] = useState<ReltaConversationSummary[]>([]);
  // Local recent conversations (the previous-chats pill for anonymous visitors;
  // signed-in users see their server history instead).
  const [localSummaries, setLocalSummaries] = useState<ReltaConversationSummary[]>([]);
  // The local id of the CURRENT conversation, exposed so the pill can highlight
  // it and drop it from the "previous" list.
  const [activeLocalId, setActiveLocalId] = useState<string>('');
  const [conversationId, setConversationId] = useState<string | null>(null);
  // Index of the assistant message currently typewriting; set in the same
  // render as the append so the message mounts already animating.
  const [streamIdx, setStreamIdx] = useState<number | null>(null);
  // The workspace is a SESSION: once opened it stays mounted (New search shows
  // an empty conversation with the rail, not the marketing hero).
  const [open, setOpen] = useState(false);

  const filtersRef = useRef<Partial<TraditionalSearchValue>>({});
  // The filters the AI itself last committed (its "intended" set). Diffing the
  // host's current filters against this yields the buyer's manual edits since
  // the last turn. Distinct from filtersRef, which also absorbs those edits.
  const lastAiFiltersRef = useRef<Partial<TraditionalSearchValue>>({});
  // Only compute a manual delta once the AI has actually set a search — before
  // that, the host's filters are the starting baseline, not "edits since".
  const aiActedRef = useRef(false);
  const countRef = useRef<number | null>(null);
  const offsetRef = useRef(0);
  const notesRef = useRef<string[]>([]);
  const seenKeysRef = useRef<string[]>([]);
  // How many property cards this anonymous session has been shown. Drives the
  // free-tier signup wall (gate on the 2nd card). Reset on a new conversation.
  const shownCardsRef = useRef(0);
  const openedTrackedRef = useRef(false);
  // Bounded display note for the NEXT turn (e.g. "every match already shown"):
  // the model must learn what the client actually rendered.
  const clientNoteRef = useRef<string | null>(null);
  const busyRef = useRef(false);
  const messagesRef = useRef<ReltaChatMessage[]>([]);
  const hydratedScopeRef = useRef<string | null>(null);
  const conversationIdRef = useRef<string | null>(null);
  // Stable local id for THIS conversation in the recent-list store. A new chat
  // gets a fresh one so the prior chat stays in the list instead of being
  // overwritten.
  const localIdRef = useRef<string>('');
  // In-flight sign-in adoption: send() awaits it so the first post-sign-in
  // turn lands on the adopted conversation instead of forking a new row.
  const adoptionRef = useRef<Promise<void> | null>(null);

  const isLoggedIn = !!user;
  const active = messages.length > 0;

  const snapshot = useCallback((): ReltaConversationState => ({
    messages: messagesRef.current,
    filters: filtersRef.current,
    count: countRef.current,
    offset: offsetRef.current,
    notes: notesRef.current,
    seenListingKeys: seenKeysRef.current,
    updatedAt: Date.now(),
  }), []);

  const persistLocal = useCallback(() => {
    if (!localIdRef.current) localIdRef.current = newLocalId();
    setActiveLocalId(localIdRef.current);
    saveLocalConversation(
      localIdRef.current,
      conversationIdRef.current,
      snapshot(),
      conversationScope,
    );
    setLocalSummaries(listLocalSummaries(conversationScope));
  }, [conversationScope, snapshot]);

  const applyState = useCallback((state: ReltaConversationState) => {
    messagesRef.current = state.messages;
    filtersRef.current = state.filters;
    countRef.current = state.count;
    offsetRef.current = state.offset;
    notesRef.current = state.notes ?? [];
    seenKeysRef.current = state.seenListingKeys ?? [];
    setMessages(state.messages);
  }, []);

  // Hydrate only the current page namespace. Dynamic route navigation may keep
  // this hook mounted, so a scope change must clear the old page conversation
  // before loading the new page's local history.
  useEffect(() => {
    if (hydratedScopeRef.current === conversationScope) return;
    hydratedScopeRef.current = conversationScope;
    messagesRef.current = [];
    filtersRef.current = {};
    lastAiFiltersRef.current = {};
    aiActedRef.current = false;
    countRef.current = null;
    offsetRef.current = 0;
    notesRef.current = [];
    seenKeysRef.current = [];
    shownCardsRef.current = 0;
    conversationIdRef.current = null;
    setMessages([]);
    setConversationId(null);
    setStreamIdx(null);
    setOpen(false);
    setGateOpen(false);
    const local = loadLocalConversation(conversationScope);
    if (local && Date.now() - local.state.updatedAt < RESUME_WINDOW_MS) {
      localIdRef.current = local.localId;
      setActiveLocalId(local.localId);
      conversationIdRef.current = local.conversationId;
      setConversationId(local.conversationId);
      applyState(local.state);
      setOpen(true);
      // Cards persist as keys only; refetch the blobs so a resume shows the
      // houses again (the /properties AI panel has no history rail to do it).
      // Guard on localId: if a seed handoff started a fresh chat while this was
      // in flight, do NOT clobber it with the resumed messages.
      const resumedId = local.localId;
      const hydration = allowListingCards
        ? hydrateCards(local.state.messages, local.state.filters, local.state.seenListingKeys ?? [])
        : Promise.resolve(local.state.messages);
      void hydration.then((m) => {
        if (localIdRef.current !== resumedId) return;
        messagesRef.current = m;
        setMessages(m);
      });
    } else {
      // Nothing fresh to resume: start a new chat, but KEEP the recent list so
      // the previous-chats pill can still reopen older conversations.
      localIdRef.current = newLocalId();
      setActiveLocalId(localIdRef.current);
    }
    setLocalSummaries(listLocalSummaries(conversationScope));
  }, [allowListingCards, applyState, conversationScope]);

  // Refresh the history rail on login, when the workspace opens, AND whenever a
  // conversation is created/adopted (conversationId change) so a fresh sign-in
  // sees its just-adopted thread without a hard refresh.
  useEffect(() => {
    if (!serverHistory || !isLoggedIn || !open) return;
    void listConversations().then(setConversations);
  }, [isLoggedIn, open, conversationId, serverHistory]);

  // Signing in mid-conversation (the gate): adopt the anonymous transcript
  // into the account once, then the server owns it.
  useEffect(() => {
    if (!isLoggedIn) return;
    setGateOpen(false);
    if (messagesRef.current.length > 0 && !conversationIdRef.current) {
      // Conversion: an anonymous conversation just authenticated.
      reltaTrack('ai_chat.signup', { trigger: 'gate' });
      adoptionRef.current = adoptAnonymousConversation(snapshot()).then((id) => {
        adoptionRef.current = null;
        // A turn sent while adoption was in flight may have created its own
        // row; never clobber an id the server already assigned.
        if (!id || conversationIdRef.current) return;
        conversationIdRef.current = id;
        setConversationId(id);
        persistLocal();
        if (optsRef.current?.serverHistory !== false) {
          void listConversations().then(setConversations);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const appendAssistant = useCallback((msg: ReltaChatMessage) => {
    messagesRef.current = [...messagesRef.current, msg];
    setMessages(messagesRef.current);
    setStreamIdx(messagesRef.current.length - 1);
  }, []);

  const endStream = useCallback(() => setStreamIdx(null), []);

  const send = useCallback(async (raw: string): Promise<boolean> => {
    const text = raw.trim();
    if (!text || busyRef.current) return false;
    const anonymousTurnLimit = optsRef.current?.anonymousTurnLimit;
    const completedAnonymousTurns = messagesRef.current.filter(
      (message) => message.role === 'assistant' && !message.error,
    ).length;
    if (
      !isLoggedIn
      && anonymousTurnLimit != null
      && completedAnonymousTurns >= anonymousTurnLimit
    ) {
      setGateOpen(true);
      reltaTrack('ai_chat.gate_shown', { reason: 'turn_limit' });
      return false;
    }
    // Free tier: no message wall. Anonymous visitors converse freely; the
    // signup gate fires later, when a SECOND property card would be shown.
    busyRef.current = true;
    setOpen(true);
    if (!openedTrackedRef.current) {
      openedTrackedRef.current = true;
      reltaTrack('ai_chat.opened', {});
    }
    if (adoptionRef.current) await adoptionRef.current;

    // Specific address / MLS number (even behind "show me the listings at ...").
    // With a live map (AI Search), let the model apply an address filter so ALL
    // units at the building surface on the map. Without one (the home page),
    // jump straight to that building's listing page.
    const addr = addressQuery(text);
    if (addr && !optsRef.current?.onFiltersChange) {
      const href = await lookupListing(addr);
      if (href) {
        busyRef.current = false;
        router.push(href);
        return true;
      }
    }

    // Bound to a host surface (the map): adopt any manual filter edits made
    // there since the last turn as this turn's baseline, and capture WHICH
    // fields the buyer changed (vs the AI's last-intended set) so the model can
    // react to a harmful edit. The server recomputes the before/after counts.
    const external = optsRef.current?.getExternalFilters?.();
    const manualEdits = external && aiActedRef.current
      ? computeManualEdits(lastAiFiltersRef.current, external)
      : null;
    if (external) filtersRef.current = { ...filtersRef.current, ...external };

    messagesRef.current = [...messagesRef.current, { role: 'user', content: text }];
    setMessages(messagesRef.current);
    setThinking(true);
    reltaTrack('ai_chat.message', { index: messagesRef.current.filter((m) => m.role === 'user').length });

    try {
      const res = await secureFetch('/api/ai-chat/respond', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversationId: conversationIdRef.current ?? undefined,
          message: text,
          surface: optsRef.current?.surface,
          surfaceLocation: optsRef.current?.surfaceLocation,
          surfaceFacet: optsRef.current?.surfaceFacet,
          seenListingKeys: seenKeysRef.current,
          clientNote: clientNoteRef.current ?? undefined,
          manualEdits: manualEdits ?? undefined,
          anonState: isLoggedIn
            ? undefined
            : {
                // Text-only continuity for the free anonymous turn.
                messages: messagesRef.current.slice(0, -1).map((m) => ({
                  role: m.role,
                  // Aggregate chart titles are safe model context and prevent
                  // a follow-up from rendering the same chart a second time.
                  content: [
                    m.content,
                    ...(m.widgets ?? []).map((widget) => `[Displayed chart: ${widget.title}]`),
                  ].join('\n\n'),
                })),
                filters: filtersRef.current,
                count: countRef.current,
                notes: notesRef.current,
              },
        }),
      });
      clientNoteRef.current = null;
      if (res.status === 429) {
        const data = (await res.json().catch(() => ({}))) as { reply?: string };
        appendAssistant({
          role: 'assistant',
          content: data.reply ?? t('aiChat.rateLimited', 'You have reached the chat limit for now. Please try again later.'),
        });
        setThinking(false);
        busyRef.current = false;
        return true;
      }
      if (res.status === 401) {
        messagesRef.current = messagesRef.current.slice(0, -1);
        setMessages(messagesRef.current);
        setGateOpen(true);
        setThinking(false);
        busyRef.current = false;
        return false;
      }
      if (!res.ok) throw new Error('respond_failed');
      const data = (await res.json()) as RespondPayload;

      if (data.conversationId && data.conversationId !== conversationIdRef.current) {
        conversationIdRef.current = data.conversationId;
        setConversationId(data.conversationId);
        if (optsRef.current?.serverHistory !== false) {
          void listConversations().then(setConversations);
        }
      }
      // Server state is authoritative.
      filtersRef.current = data.filters as Partial<TraditionalSearchValue>;
      // Push the AI's resulting search to the host surface (the map/list/URL),
      // and remember it as the AI's last-intended set for the next turn's
      // manual-edit diff.
      if (data.searchChanged) {
        lastAiFiltersRef.current = data.filters as Partial<TraditionalSearchValue>;
        aiActedRef.current = true;
        optsRef.current?.onFiltersChange?.(filtersRef.current);
      }
      // Apply any map directives (layer toggles) the turn produced.
      if (data.mapCommands?.length) optsRef.current?.onMapCommands?.(data.mapCommands);
      countRef.current = data.count;
      offsetRef.current = data.offset;
      notesRef.current = appendNote(notesRef.current, extractNote(text, data.action));

      let matches: MlsListingCardData[] | undefined;
      let reasons: string[] | undefined;
      let mapsUrl: string | undefined;
      let facts: string | undefined;
      let reply = data.reply;

      if (data.action === 'commute_handoff') {
        mapsUrl = mapsUrlFor(filtersRef.current);
      } else if (
        data.action === 'focus'
        && optsRef.current?.allowListingCards !== false
      ) {
        const lastMatches = [...messagesRef.current].reverse().find((m) => m.matches?.length)?.matches;
        const focused = lastMatches?.[data.matchIndex ?? 0];
        if (focused) {
          facts = matchFacts(focused, t);
          matches = [focused];
          reltaTrack('ai_chat.match_focus', { listing_key: focused.listingKey });
          // Fly the live map to the focused home (AI Search view; no-op on home).
          if (focused.latitude != null && focused.longitude != null) {
            optsRef.current?.onMapCommands?.([
              { type: 'focusListing', listingKey: focused.listingKey, lat: focused.latitude, lng: focused.longitude },
            ]);
          }
        } else {
          reply = `${reply}\n\n${t('aiChat.noFocus', 'No home has been shown in this conversation yet. Describe what you are looking for and Relta will line one up.')}`.trim();
        }
      }

      if (data.mortgage) {
        const mg = data.mortgage;
        reply = `${reply}\n\n${t('aiChat.mortgageLine', 'On a {{price}} home with {{down}} down ({{downPct}}%) at {{rate}}% over {{years}} years, the estimated payment is about {{pi}}/month for principal and interest, or roughly {{total}}/month with property tax included. Estimates only: the stress-test tool gives the full picture.', {
          price: cad(mg.price),
          down: cad(mg.downPayment),
          downPct: mg.downPct,
          rate: mg.rate,
          years: mg.years,
          pi: cad(mg.monthlyPrincipalAndInterest),
          total: cad(mg.monthlyTotal),
        })}`.trim();
      }

      // The server resolved presentation from executed tools.
      if (data.presented && optsRef.current?.allowListingCards !== false) {
        const { results: page } = await matchesFor(filtersRef.current, offsetRef.current);
        // Never re-present a home the buyer has already seen this conversation.
        // Cross-feed twins (Bridge + DDF copies of the SAME home) carry
        // different listing keys but the same MLS number, so the ledger holds
        // both tokens and presentation filters on both.
        const mlsToken = (m: MlsListingCardData) => (m.listingId ? `mls:${m.listingId}` : null);
        const unseen = page.filter((m) =>
          !seenKeysRef.current.includes(m.listingKey)
          && !(mlsToken(m) && seenKeysRef.current.includes(mlsToken(m)!)));
        const found = unseen.slice(0, MATCHES_PER_TURN);
        reasons = filterChips(filtersRef.current);
        // Free-tier signup wall: an anonymous visitor sees the first home free.
        // When a SECOND card would appear, withhold it behind the gate. The card
        // is NOT marked seen, so it presents right after they sign in.
        if (!isLoggedIn && found.length > 0 && shownCardsRef.current >= ANON_FREE_CARDS) {
          matches = undefined;
          setGateOpen(true);
          reltaTrack('ai_chat.gate_shown', { reason: 'second_card' });
          reply = `${reply}\n\n${t('aiChat.gatePreview', 'Sign in to see this home and keep going. It takes a moment and your conversation carries over.')}`.trim();
        } else if (found.length > 0) {
          for (const m of found) {
            const tokens = [m.listingKey, ...(mlsToken(m) ? [mlsToken(m)!] : [])];
            seenKeysRef.current = [...seenKeysRef.current, ...tokens].slice(-MAX_SEEN_KEYS);
          }
          shownCardsRef.current += found.length;
          matches = found;
          // Bring the map onto the home being presented in the conversation.
          const top = found[0];
          if (top?.latitude != null && top?.longitude != null) {
            optsRef.current?.onMapCommands?.([
              { type: 'focusListing', listingKey: top.listingKey, lat: top.latitude, lng: top.longitude },
            ]);
          }
          reltaTrack('ai_chat.matches_shown', { result_count: countRef.current ?? undefined });
        } else if (page.length > 0 || (countRef.current ?? 0) > 0) {
          // Everything matching has been shown (or the card fetch hiccuped):
          // say so honestly instead of claiming nothing matches, and tell the
          // MODEL on the next turn so it stops promising more homes.
          reply = `${reply}\n\n${t('aiChat.allSeen', 'That covers every current match for this search. Adjusting one thing, like budget or area, will surface new homes.')}`.trim();
          clientNoteRef.current = 'all_seen';
        } else {
          reply = `${reply}\n\n${t('aiChat.noMatches', 'No active listings match all of that right now. Loosening one thing, like budget or area, usually opens it back up.')}`.trim();
        }
      } else if (data.searchChanged) {
        reasons = filterChips(filtersRef.current);
      }

      appendAssistant({
        role: 'assistant',
        content: reply,
        facts,
        matches,
        reasons,
        mapsUrl,
        proposals: data.proposals?.length ? data.proposals : undefined,
        agentCta: data.agentCta || undefined,
        widgets: data.widgets?.length ? data.widgets : undefined,
        revertSuggestion: data.revertSuggestion ?? undefined,
      });
      if (data.proposals?.length) {
        for (const p of data.proposals) reltaTrack('ai_chat.action_proposed', { capability: p.capability });
      }
      persistLocal();
    } catch {
      appendAssistant({
        role: 'assistant',
        error: true,
        content: t('aiChat.error', 'Something went wrong on this turn. Try that again in a moment.'),
      });
    } finally {
      setThinking(false);
      busyRef.current = false;
    }
    return true;
  }, [appendAssistant, isLoggedIn, persistLocal, router, t]);

  // Re-run the turn that produced the trailing error bubble.
  const retry = useCallback(async (): Promise<boolean> => {
    const msgs = messagesRef.current;
    const last = msgs[msgs.length - 1];
    const prev = msgs[msgs.length - 2];
    if (!last?.error || prev?.role !== 'user') return false;
    messagesRef.current = msgs.slice(0, -2);
    setMessages(messagesRef.current);
    return send(prev.content);
  }, [send]);

  // Buyer tapped "Restore" on a revert offer: apply the restored set to the
  // host (map/list/URL) and adopt it as the AI's baseline so the next turn does
  // NOT re-flag it as a fresh manual edit. No server round-trip: the server
  // memory + profile reconcile on the buyer's next message.
  const applyRevert = useCallback((filters: Partial<TraditionalSearchValue>) => {
    filtersRef.current = { ...filtersRef.current, ...filters };
    lastAiFiltersRef.current = filtersRef.current;
    optsRef.current?.onFiltersChange?.(filtersRef.current);
  }, []);

  const newConversation = useCallback(() => {
    messagesRef.current = [];
    filtersRef.current = {};
    lastAiFiltersRef.current = {};
    aiActedRef.current = false;
    countRef.current = null;
    offsetRef.current = 0;
    notesRef.current = [];
    seenKeysRef.current = [];
    shownCardsRef.current = 0;
    conversationIdRef.current = null;
    // Fresh local id so the PRIOR conversation stays in the recent list (it was
    // already saved each turn) instead of being overwritten. Never clear the list.
    localIdRef.current = newLocalId();
    setActiveLocalId(localIdRef.current);
    setMessages([]);
    setConversationId(null);
    setStreamIdx(null);
    setLocalSummaries(listLocalSummaries(conversationScope));
  }, [conversationScope]);

  const openConversation = useCallback(async (id: string) => {
    // Local recent chat (anon, or a client copy): load from localStorage.
    const localBlob = loadLocalById(id, conversationScope);
    if (localBlob) {
      localIdRef.current = localBlob.localId;
      setActiveLocalId(localBlob.localId);
      conversationIdRef.current = localBlob.conversationId;
      setConversationId(localBlob.conversationId);
      applyState(localBlob.state);
      setOpen(true);
      const hydrated = allowListingCards
        ? await hydrateCards(localBlob.state.messages, localBlob.state.filters, localBlob.state.seenListingKeys ?? [])
        : localBlob.state.messages;
      if (localIdRef.current !== localBlob.localId) return; // switched away mid-fetch
      messagesRef.current = hydrated;
      setMessages(hydrated);
      return;
    }
    // Server-owned (signed-in): fetch, then mirror into the local recent list.
    const conv = await fetchConversation(id);
    if (!conv) return;
    conversationIdRef.current = conv.id;
    setConversationId(conv.id);
    applyState(conv.state);
    setOpen(true);
    // v2 state is text-only: re-hydrate every presented home by key.
    const hydrated = allowListingCards
      ? await hydrateCards(conv.state.messages, conv.state.filters, conv.state.seenListingKeys ?? [])
      : conv.state.messages;
    if (conversationIdRef.current !== conv.id) return; // switched away mid-fetch
    messagesRef.current = hydrated;
    setMessages(hydrated);
    persistLocal();
  }, [allowListingCards, applyState, conversationScope, persistLocal]);

  const removeConversation = useCallback(async (id: string) => {
    if (loadLocalById(id, conversationScope)) {
      removeLocalConversation(id, conversationScope);
      setLocalSummaries(listLocalSummaries(conversationScope));
    } else {
      await deleteConversationApi(id);
      setConversations((prev) => prev.filter((c) => c.id !== id));
    }
    if (id === conversationIdRef.current || id === localIdRef.current) newConversation();
  }, [conversationScope, newConversation]);

  return {
    messages,
    thinking,
    active,
    open,
    send,
    retry,
    applyRevert,
    gateOpen,
    setGateOpen,
    isLoggedIn,
    conversations,
    // Unified list for the previous-chats pill: server history when signed in,
    // the local recent list for anonymous visitors.
    recentConversations: isLoggedIn && serverHistory ? conversations : localSummaries,
    conversationId,
    localId: activeLocalId,
    newConversation,
    openConversation,
    removeConversation,
    streamIdx,
    endStream,
  };
}
